import { useEffect, useRef, useState } from "react";
import { SearchBox, type SearchBoxRef } from "@/search/SearchBox";
import { useSearchIcd10Codes } from "@/clinical-codes/useSearchIcd10Codes";
import { useSearchOpcs4Codes } from "@/clinical-codes/useSearchOpcs4Codes";
import { InlineLoading } from "./LoadingOverlay";
import type { CodeType } from "@/clinical-codes/types/ClinicalCodeTypes";
import type { OpcsVersion } from "@/clinical-codes/opcsVersion";

type SelectedCode = {
  id: string;
  code: string;
  description: string;
  type: CodeType;
};

type InlineCodeSearchProps = {
  section: "procedures" | "diagnoses";
  onSelect: (code: SelectedCode) => void;
  onDismiss: () => void;
  opcsVersion?: OpcsVersion;
};

export function InlineCodeSearch({ section, onSelect, onDismiss, opcsVersion }: InlineCodeSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<SearchBoxRef>(null);
  const isProcedure = section === "procedures";

  // Auto-focus the search input on mount
  useEffect(() => {
    searchBoxRef.current?.focus();
  }, []);

  const icd10 = useSearchIcd10Codes(isProcedure ? "" : searchQuery);
  const opcs4 = useSearchOpcs4Codes(isProcedure ? searchQuery : "", undefined, opcsVersion);

  const isLoading = isProcedure ? opcs4.isLoading : icd10.isLoading;

  const codes: SelectedCode[] = isProcedure
    ? (opcs4.codes || []).map((c) => ({
        id: c.id,
        code: c.code,
        description: c.description,
        type: "OPCS-4" as const,
      }))
    : (icd10.codes || []).map((c) => ({
        id: c.id,
        code: c.code,
        description: c.description,
        type: "ICD-10" as const,
      }));

  // Click-outside dismissal
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onDismiss();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onDismiss]);

  // Escape key dismissal
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onDismiss();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onDismiss]);

  const borderColor = isProcedure
    ? "border-blue-300 focus-within:border-blue-500"
    : "border-green-300 focus-within:border-green-500";

  return (
    <div
      ref={containerRef}
      className={`relative rounded-lg border-2 ${borderColor} bg-white p-2 shadow-md transition-colors`}
    >
      <SearchBox ref={searchBoxRef} searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      <div className="mt-1 max-h-48 overflow-y-auto">
        {isLoading ? (
          <InlineLoading message="Searching..." />
        ) : (
          <div className="space-y-0.5">
            {codes.map((code) => (
              <div
                key={code.id}
                className="cursor-pointer rounded p-1.5 transition-colors hover:bg-gray-50 active:bg-gray-100"
                onClick={() => onSelect(code)}
              >
                <span className="font-mono text-sm font-medium">{code.code}</span>
                <span className="ml-2 text-xs text-gray-600">{code.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
